import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { buildListEmptyMessage } from '../../lib/build-list-empty-message.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { countVendorListFilters } from '../../modules/vendors/count-vendor-list-filters.js';
import { isVendorSelectionUnfiltered } from '../../modules/vendors/is-vendor-selection-unfiltered.js';
import {
  GET_VENDORS,
  type GetVendorsQueryResult,
  type GetVendorsQueryVariables,
  type VendorListItem,
  type VendorWhere,
} from '../../modules/vendors/operations.js';
import { useVendorFormOptions } from '../../modules/vendors/use-vendor-form-options.js';
import { useVendorListFilters } from '../../modules/vendors/use-vendor-list-filters.js';
import { VendorFiltersMenu } from '../../modules/vendors/vendor-filters-menu.js';
import { makeVendorColumns } from '../../modules/vendors/vendor-list-columns.js';
import { buildVendorListWhere } from '../../modules/vendors/vendor-list-where.js';

// Column id -> Prisma orderBy path, verified against SORTABLE_FIELDS/SORTABLE_RELATIONS.Vendor.
export const VENDOR_SORT_MAP: Record<string, string[]> = {
  name: ['name'],
  activeFlag: ['activeFlag'],
  vendorStatus: ['statusId'],
  currency: ['currency', 'name'],
  paymentTerms: ['paymentTerms', 'name'],
  leadTime: ['leadTime'],
  creditLimit: ['creditLimit'],
};

export const Route = createFileRoute('/vendors/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: VendorsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function VendorsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('vendor:edit');
  const columns = useMemo(() => makeVendorColumns(canEdit), [canEdit]);
  // A ref, since useListTable is declared below and owns the pagination reset the filters call.
  const resetPaginationRef = useRef<() => void>(() => {});
  const resetPagination = useCallback(() => resetPaginationRef.current(), []);
  const {
    activeStatuses,
    statusIds,
    paymentTermsIds,
    onActiveStatusesChange,
    onStatusIdsChange,
    onPaymentTermsIdsChange,
    onClearAll,
  } = useVendorListFilters(resetPagination);
  const { paymentTerms, vendorStatuses } = useVendorFormOptions(true);
  const filterSelection = useMemo(
    () => ({ activeStatuses, statusIds, paymentTermsIds }),
    [activeStatuses, statusIds, paymentTermsIds],
  );
  const where = useMemo(() => buildVendorListWhere(filterSelection), [filterSelection]);

  const {
    rows: vendors,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<
    GetVendorsQueryResult,
    GetVendorsQueryVariables,
    VendorListItem,
    VendorWhere | undefined
  >({
    query: GET_VENDORS,
    extractConnection: (data) => data?.vendors,
    defaultSort: [{ id: 'name', desc: false }],
    sortMap: VENDOR_SORT_MAP,
    sortTiebreakerField: 'id',
    where,
  });

  useEffect(() => {
    resetPaginationRef.current = tableProps.onFirstPage;
  });

  const newVendorAction = (
    <PermissionGuard
      required={['vendor:edit']}
      pass={
        <Link to="/vendors/new" className={buttonVariants({})}>
          + New Vendor
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New Vendor
        </span>
      }
    />
  );

  // Only a view no filter could widen may show the full-page empty state, since it also hides the filters.
  const isUnfilteredView = !search && isVendorSelectionUnfiltered(filterSelection);
  const hasActiveFilters = countVendorListFilters(filterSelection) > 0;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Vendors</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Manage supplier records — identity, commercial terms, and vendor part associations.
        </DimeCardDescription>
        <DimeCardAction>{newVendorAction}</DimeCardAction>
      </DimeCardHeader>
      {error && vendors.length === 0 ? (
        <ListErrorState
          title="Could not load vendors"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && vendors.length === 0 && isUnfilteredView ? (
        <ListEmptyState
          icon={<Building2 />}
          title="No vendors yet"
          description="Add your first vendor to start tracking purchase orders and commercial terms."
          action={newVendorAction}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={vendors}
          maxBodyHeight="viewport"
          searchColumn="name"
          searchPlaceholder="Search by name…"
          filters={
            <VendorFiltersMenu
              activeStatuses={activeStatuses}
              onActiveStatusesChange={onActiveStatusesChange}
              vendorStatuses={vendorStatuses}
              statusIds={statusIds}
              onStatusIdsChange={onStatusIdsChange}
              paymentTerms={paymentTerms}
              paymentTermsIds={paymentTermsIds}
              onPaymentTermsIdsChange={onPaymentTermsIdsChange}
              onClearAll={onClearAll}
            />
          }
          showViewOptions
          onRowClick={(vendor) => {
            void navigate({
              to: '/vendors/$vendorId',
              params: { vendorId: vendor.id },
            });
          }}
          emptyMessage={buildListEmptyMessage('vendors', search, hasActiveFilters)}
          loadingRowCount={tableProps.pageSize}
          exportFileName="vendors"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
